/**
 * Hook for loading and saving the global answer-analysis provider setting.
 *
 * @module useAnswerAnalysisSettings
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  getAnswerAnalysisSettings,
  updateAnswerAnalysisProvider,
  type AnswerAnalysisSettings,
} from '@/lib/answerAnalysisSettingsApi';

export const ANSWER_ANALYSIS_SETTINGS_QUERY_KEY = ['admin-answer-analysis-settings'] as const;

export interface UseAnswerAnalysisSettingsResult {
  readonly settings: AnswerAnalysisSettings | null | undefined;
  readonly selectedProvider: string;
  readonly setSelectedProvider: (provider: string) => void;
  readonly isLoading: boolean;
  readonly isSaving: boolean;
  readonly isDirty: boolean;
  readonly save: () => void;
}

export function useAnswerAnalysisSettings(): UseAnswerAnalysisSettingsResult {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedProvider, setSelectedProvider] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: ANSWER_ANALYSIS_SETTINGS_QUERY_KEY,
    queryFn: getAnswerAnalysisSettings,
  });

  const saveMutation = useMutation({
    mutationFn: async (provider: string) => {
      const result = await updateAnswerAnalysisProvider(provider);
      if (!result) {
        throw new Error('Failed to update answer analysis provider');
      }
      return result;
    },
    onSuccess: (result) => {
      queryClient.setQueryData(ANSWER_ANALYSIS_SETTINGS_QUERY_KEY, result);
      setSelectedProvider(result.provider);
      toast.success(t('admin.answersManager.providerSaved'));
    },
    onError: () => {
      toast.error(t('admin.answersManager.providerSaveFailed'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ANSWER_ANALYSIS_SETTINGS_QUERY_KEY });
    },
  });

  const isSaving = saveMutation.isPending;
  const savedProvider = settings?.provider ?? '';

  useEffect(() => {
    if (!isSaving && savedProvider) {
      setSelectedProvider(savedProvider);
    }
  }, [savedProvider, isSaving]);

  const isDirty = Boolean(savedProvider && selectedProvider && selectedProvider !== savedProvider);

  const save = () => {
    if (!selectedProvider || !isDirty || isSaving) {
      return;
    }
    saveMutation.mutate(selectedProvider);
  };

  return {
    settings,
    selectedProvider: selectedProvider || savedProvider || settings?.defaultProvider || 'openai',
    setSelectedProvider,
    isLoading,
    isSaving,
    isDirty,
    save,
  };
}
